import { type IForeignLink } from '@Model';
import {
    type Awaitable,
    Badge,
    type Column,
    EntityLinkingModal,
    type IFrameEditShellProps,
    type IIdWithDisplay,
    type Index,
    type IWithStatus,
    LinkableCell,
    type ToolbarElements,
    useEntityLinker,
    type UseEntityLinkerOptions,
    useOnTheFlyModal,
} from '@synqo/frontend-component-library';
import { type FC, memo, type MouseEventHandler, useCallback, useMemo, useRef } from 'react';

type StagedLinkingBadgeProps = {
    displayString: string;
};

const StagedLinkingBadge = memo(function StagedLinkingBadge({ displayString }: StagedLinkingBadgeProps) {
    return (
        <Badge variant="danger" type="input" width="fitContent">
            {displayString}
        </Badge>
    );
});

export interface SharedLinkableCellProps<TParent> {
    rootObject: TParent;
    baseApiObjectPath: string;
    propertyName: Index<TParent>;
    /** The link to display. May sit nested inside `propertyName`; linking always writes to `propertyName`. */
    link: IForeignLink;
    onLinked: (updatedRootObject: TParent) => Awaitable<void>;
}

export interface BaseLinkableCellProps<TParent, TApiObject extends IIdWithDisplay & IWithStatus>
    extends SharedLinkableCellProps<TParent> {
    overlayRenderer: FC<IFrameEditShellProps<TApiObject>>;
    columns: Column<TApiObject>[];
    badgeKeyPrefix: string;
}

export function BaseLinkableCell<TParent, TApiObject extends IIdWithDisplay & IWithStatus>({
    rootObject,
    baseApiObjectPath,
    propertyName,
    link,
    overlayRenderer,
    columns,
    badgeKeyPrefix,
    onLinked,
}: BaseLinkableCellProps<TParent, TApiObject>) {
    const { display_string, foreign_key } = link;

    const fieldLinkingOptions = useMemo<UseEntityLinkerOptions<TParent, TApiObject>>(() => {
        const actionItems = display_string
            ? [<StagedLinkingBadge key={`${badgeKeyPrefix}_info`} displayString={display_string} />]
            : undefined;

        return {
            rootObject,
            baseApiObjectPath,
            propertyName: propertyName as Index<TParent>,
            overlayRenderer,
            frameOptions: {
                actionItems,
            },
            onLinked,
        };
    }, [rootObject, baseApiObjectPath, propertyName, display_string, overlayRenderer, badgeKeyPrefix, onLinked]);

    const { api, props } = useEntityLinker<TParent, TApiObject>(fieldLinkingOptions);

    const modalIdRef = useRef('');
    const { openOnTheFlyModal, closeOnTheFlyModals } = useOnTheFlyModal();

    const closeModal = useCallback(() => {
        if (modalIdRef.current) {
            closeOnTheFlyModals(modalIdRef.current);
            modalIdRef.current = '';
        }
    }, [closeOnTheFlyModals]);

    const handleLinkedCellClick = useCallback(() => {
        if (!foreign_key) {
            return;
        }

        const OverlayRenderer = overlayRenderer;
        modalIdRef.current = openOnTheFlyModal({
            child: (
                <OverlayRenderer
                    primaryKey={foreign_key}
                    embedded
                    canEdit={false}
                    canCreate={false}
                    canDelete={false}
                    events={{
                        onClose: closeModal,
                        onBack: undefined,
                    }}
                />
            ),
        });
    }, [foreign_key, closeModal, openOnTheFlyModal, overlayRenderer]);

    const toolbarElements = useMemo((): ToolbarElements => {
        const upstreamElements = props.toolbarElements;

        if (!display_string) {
            return upstreamElements ?? [];
        }

        const badgeElement = <StagedLinkingBadge key={`${badgeKeyPrefix}_info`} displayString={display_string} />;

        if (Array.isArray(upstreamElements) && upstreamElements.length > 0) {
            return [...upstreamElements, badgeElement];
        }

        return [badgeElement];
    }, [props.toolbarElements, display_string, badgeKeyPrefix]);

    const stopPropagation = useCallback<MouseEventHandler>((event) => event.stopPropagation(), []);

    return (
        <div onClick={stopPropagation}>
            <LinkableCell
                label={link.display_string}
                itemId={link.foreign_key}
                onClick={handleLinkedCellClick}
                onLinkClick={api.openModal}
            />
            <EntityLinkingModal
                columns={columns}
                loader={api.loadLinkableObjects}
                {...props}
                toolbarElements={toolbarElements}
            />
        </div>
    );
}
